/**
 * iProov event handler for workflow face verification flow.
 * Mirrors useIProovEvents.ts from the IVA flow — mounts listeners as soon as
 * iproovToken is available (not gated on state), same as IVA pattern.
 *
 * No consent logic — workflow flow has no IVA session token / consent requirement.
 *
 * On ready    → logger only
 * On started  → setState("capturing")
 * On passed   → onVerificationPassed()
 * On failed/error/canceled/permission_denied → onVerificationError()
 */

import { useEffect, type RefObject } from "react";
import { logger } from "../../shared/utils/logger";
import type { WorkflowFaceVerificationState } from "./WorkflowFaceVerification";

interface UseWorkflowIProovEventsParams {
  iproovRef: RefObject<HTMLElement>;
  iproovToken: string | null;
  setState: (state: WorkflowFaceVerificationState) => void;
  setProgress: (progress: { percent: number; message: string }) => void;
  onVerificationPassed: () => void;
  onVerificationError: (errorCode: string, message: string) => void;
}

export function useWorkflowIProovEvents({
  iproovRef,
  iproovToken,
  setState,
  setProgress,
  onVerificationPassed,
  onVerificationError,
}: UseWorkflowIProovEventsParams) {
  useEffect(() => {
    // Mount listeners as soon as iProov element and token are available —
    // same pattern as IVA useIProovEvents (not gated on state).
    if (!iproovRef.current || !iproovToken) return;

    const iproovElement = iproovRef.current;

    logger.log("[WorkflowIProov] Attaching iProov event listeners");

    const handleReady = () => {
      logger.log("[WorkflowIProov] Ready");
    };

    const handleStarted = () => {
      logger.log("[WorkflowIProov] Started");
      setState("capturing");
    };

    const handleStreaming = () => {
      logger.log("[WorkflowIProov] Streaming");
    };

    const handleProgress = (event: Event) => {
      const { progress, message } = (event as CustomEvent).detail ?? {};
      logger.log("[WorkflowIProov] Progress:", progress, message);
      setProgress({ percent: progress ?? 0, message: message ?? "" });
    };

    const handlePassed = () => {
      logger.log("[WorkflowIProov] Passed — triggering post-capture flow");
      onVerificationPassed();
    };

    const handleFailed = (event: Event) => {
      // v6: failure reasons arrive in `reasons[]`. The message is localized via
      // our iProov `language` JSON (failure_<code> keys), so read reasons[0]
      // `localized_description` directly. Mirrors useIProovEvents.handleFailed.
      const { reasons = [] } = (event as CustomEvent).detail ?? {};
      const primary = reasons[0];
      logger.warn("[WorkflowIProov] Failed:", primary?.feedback_code);
      onVerificationError("iproov_failed", primary?.localized_description || "Face liveness verification failed");
    };

    const handleError = (event: Event) => {
      // v6: error reasons arrive in `reasons[]` (localized via our error_<code> keys).
      const { reasons = [] } = (event as CustomEvent).detail ?? {};
      const primary = reasons[0];
      logger.error("[WorkflowIProov] Error:", primary?.feedback_code, primary?.localized_description);
      onVerificationError("iproov_failed", primary?.localized_description || "iProov error occurred");
    };

    const handleCanceled = () => {
      logger.log("[WorkflowIProov] Canceled by user");
      onVerificationError("iproov_failed", "User cancelled");
    };

    const handleUnsupported = () => {
      logger.error("[WorkflowIProov] Device/browser unsupported");
      onVerificationError("iproov_failed", "Device or browser not supported");
    };

    const handlePermissionDenied = () => {
      logger.error("[WorkflowIProov] Camera permission denied");
      onVerificationError("iproov_failed", "Camera access denied");
    };

    iproovElement.addEventListener("ready", handleReady);
    iproovElement.addEventListener("started", handleStarted);
    iproovElement.addEventListener("streaming", handleStreaming);
    iproovElement.addEventListener("progress", handleProgress);
    iproovElement.addEventListener("passed", handlePassed);
    iproovElement.addEventListener("failed", handleFailed);
    iproovElement.addEventListener("error", handleError);
    iproovElement.addEventListener("canceled", handleCanceled);
    iproovElement.addEventListener("unsupported", handleUnsupported);
    iproovElement.addEventListener("permission_denied", handlePermissionDenied);

    return () => {
      logger.log("[WorkflowIProov] Cleaning up iProov event listeners");
      iproovElement.removeEventListener("ready", handleReady);
      iproovElement.removeEventListener("started", handleStarted);
      iproovElement.removeEventListener("streaming", handleStreaming);
      iproovElement.removeEventListener("progress", handleProgress);
      iproovElement.removeEventListener("passed", handlePassed);
      iproovElement.removeEventListener("failed", handleFailed);
      iproovElement.removeEventListener("error", handleError);
      iproovElement.removeEventListener("canceled", handleCanceled);
      iproovElement.removeEventListener("unsupported", handleUnsupported);
      iproovElement.removeEventListener("permission_denied", handlePermissionDenied);
    };
  }, [iproovRef, iproovToken, setState, setProgress, onVerificationPassed, onVerificationError]);
}
